import { FileUploadResponse } from "../services/apiService";
import { ClaimDetailResponse } from "./claims";

export const STOP_RESPONDING_MESSAGE = 'Stop responding';

export enum WEBSOCKET_MESSAGE_TYPES {
    pong = 'pong',
    heartbeat = 'heartbeat',
    welcome = 'welcome',
    token_refresh_required = 'token_refresh_required',
    config_flags = 'config_flags',
    release_note_published = 'release_note_published',
    ticket_changed = 'ticket_changed',
    chat_created = 'chat_created',
    chat_closed = 'chat_closed',
    message_ack = 'message_ack',
    status_message = 'status_message',
    task_in_progress = 'task_in_progress',
    partial = 'partial',
    stream = 'stream',
    final = 'final',
    complete = 'complete',
    agent_result = 'agent_result',
    file_uploaded = 'file_uploaded',
    document_output_available = 'document_output_available',
    user_message = 'user_message',
    error = 'error',
}

export interface IFileAttachment {
    id: string;
    name?: string
    display_name?: string
    original_name: string;
    file_name: string;
    file_size: number;
    file_type: string;
    uploaded_at: string;
    file_url: string;
    url?: string;
    is_processed: boolean;
    processed_content_preview?: string;
    deleted: boolean;
    deleted_at?: string | null;
    page_rotation_mapping?: IPageRotation
    // UI only
    page?: number
    is_pinned?: boolean;
    /** True when `id` is a document id (policy/contract citation), fetched/downloaded via
     * the documents endpoint (fetchDocumentById/downloadDocumentById) instead of the
     * uploaded-file endpoint (fetchFileContentById/forceDownloadFile). */
    is_document?: boolean;
}

/** One version of a generated document (e.g. appeal letter), as listed by
 * GET /chats/<id>/output-documents/ — the list carries no body. */
export interface ChatLetterSummary {
    id: string;
    agent_name: string;
    display_name: string;
    generation: number;
    is_pinned: boolean;
    message: string; // Id of the message that produced the letter
    source: string;
    type: string;
    created_at: string;
    updated_at: string;
}

/** WS event: the LLM added a document version. Carries no body — fetch it. */
export interface DocumentOutputAvailableEvent {
    type: 'document_output_available';
    chat_id: string;
    version: number;
    title: string;
    document_type: string;
}

/** One version with its body, from GET .../output-documents/latest/ or .../<generation>/. */
export interface ChatLetterOutput extends ChatLetterSummary {
    content: string; // Letter HTML, shown and edited in the letter editor
    // UI only: chat the letter was opened in (set by ChatStore.setLetterToPreview),
    // so its edits are saved to that chat even after the user switches away
    chatId?: string;
}

export interface IPageRotation {
    [page_index: number]: number;
}

export interface IMessage {
    id?: string;
    ref_id?: string; // Turn correlation id echoed from the request; groups a turn's partial/stream/final
    type?: 'partial' | 'complete' | 'final' | 'stream' | 'message_ack' | 'task_in_progress' | 'status_message';
    channel: string;
    data: string | IMessageClaimDetail;
    sender: 'me' | 'other';
    timestamp: number;
    created_at?: string;
    files?: IFileAttachment[]; // Array of file attachments with full details
    agent_name?: string; // Name of the agent that sent the message
    metadata?: IMessageMetadata
    contextWarning?: boolean;
    page_rotation_mapping?: IPageRotation
    // Feedback (thumbs up/down)
    feedback_comment?: string
    feedback_rating?: 'up' | 'down'
    // Per-message author (REV-2177): set on role=user messages so cloned/shared
    // chats show the original sender. Empty author_avatar = the viewer's own message.
    author_name?: string;
    author_avatar?: string;
}

/** The rename modal's target in {@link SidebarFileViewer} — a file attachment or the
 * generated letter, plus the name to prefill the input with. */
export type IRenameFileTarget =
    | { kind: 'file'; file: IFileAttachment; currentName: string }
    | { kind: 'letter'; letter: ChatLetterSummary; currentName: string };

export interface IMessageClaimDetail {
    title?: string;
    section_labels: {
        [key: string]: string;
    };
    section_display_order: string[];
    overview: {
        claim_id: string;
        patient_name: string;
        payer_name: string;
        total_billed: number;
        total_allowed: number;
        total_paid: number;
        total_patient_responsibility: number;
        service_line_count: number;
        overall_classification: string;
    };
    line_analyses: {
        line_number: number;
        cpt_hcpcs_code: string;
        cpt_description: string;
        billed_amount: number;
        allowed_amount: number;
        paid_amount: number;
        carc_codes: string[];
        rarc_codes: string[];
        line_classification: string;
        denial_category: string;
        contracted_rate: number | null;
        payment_validation_result: string;
        coding_finding_ids: string[];
        valid_denial: boolean;
        valid_denial_reason: string;
        recovery_opportunity: number;
    }[];
    payment_analysis: {
        line_number: number;
        cpt_hcpcs_code: string;
        cpt_description: string;
        billed_amount: number;
        allowed_amount: number;
        paid_amount: number;
        contracted_rate: number | null;
        payment_validation_result: string;
        carc_codes: string[];
        rarc_codes: string[];
        line_classification: string;
        coding_issues: string[];
        paid_correctly: boolean | null;
        payment_note: string;
    }[];
    coding_findings: {
        finding_id: string;
        check_category: string;
        severity: string;
        affected_lines: number[];
        description: string;
        reference_source: string;
        denial_impact: string;
        denial_impact_detail: string;
        suggested_correction: string | null;
        carc_rarc_correlation: string;
    }[];
    issue_summary: string;
    resolutions: {
        line_number: number;
        resolution: string;
        strategy: string;
        coding_finding_ids: string[];
        policy_citation: string;
        confidence: string;
      }[];
    overturn_scores: {
        line_number: number;
        probability: string;
        percentage_range: string;
        rationale: string[];
    }[];
    work_queue_entry: {
        claim_id: string;
        priority: string;
        classification: string;
        recovery_opportunity: string;
        recommended_action: string;
        overturn_probability: string;
        effort_level: string;
        time_sensitivity: string;
    };
    executive_summary: string;
}

export enum AppealSuccessLikelihood {
    HIGH = 'High',
    MEDIUM = 'Medium',
    LOW = 'Low',
}

export enum DenialClass {
    MEDICAL_NECESSITY = 'Medical Necessity',
    PRE_AUTHORIZATION = 'Pre-Authorization Required',
    TIMELY_FILING = 'Timely Filing',
    CODING_ISSUE = 'Coding Issue',
    CONTRACTUAL_OBLIGATION = 'Contractual Obligation',
    MISSING_INFORMATION = 'Missing Information',
    ADMINISTRATIVE_ERROR = 'Administrative Processing Error',
    OTHER = 'Other',
}

export enum PolicyApplicability {
    APPLICABLE = 'APPLICABLE',
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    AMBIGUOUS = 'AMBIGUOUS',
}

export interface IPolicyCitation {
    policy_code: string;
    policy_name: string;
    payer: string;
    applicability: PolicyApplicability;
    relevant_section?: string | null;
    document_id: string;
    applicability_rationale?: string | null;
    source: string;
}

export interface ICorrectionGuidance {
    affected_lines: number[];
    correction_trigger: string;
    error_description: string;
    correction_steps: string[];
    resubmission_instructions?: string | null;
}

export interface IClinicalQuote {
    quote_text: string;
    source_document: string;
    source_date: string;
    source_author?: string | null;
    source_section?: string | null;
    supports_line_number: number;
    relevance_summary: string;
}

export interface IAppealArgument {
    argument_id: string;
    argument_type: string;
    description: string;
    strength: string;
    evidence_references: string[];
}

export interface IProposedApproach {
    primary_issue: string;
    recommended_angle: string;
    key_evidence: string[];
}

// Alternate backend shape for an object section: explicit column labels plus
// label/value rows, e.g. { columns: ["Metric", "Assessment"], fields: [{ label, value }] }.
export interface IColumnsFieldsObject {
    columns: string[];
    fields: { label: string; value: unknown }[];
}

export interface IMessageAppealStrategy {
    title?: string;
    
    disposition: string;
    exit_reason?: string | null;
    lines_for_appeal: number[];
    lines_for_correction: number[];
    correction_guidance?: ICorrectionGuidance | null;

    denial_classification: DenialClass;

    payer_cited_policy?: IPolicyCitation | null;
    supporting_policies: IPolicyCitation[];
    contradicting_policies: IPolicyCitation[];
    policies_found: boolean;

    overall_documentation_strength?: string | null;
    lines_with_strong_documentation: number;
    lines_with_absent_documentation: number;
    diagnosis_mismatch_count: number;
    evidence_types_absent_by_line: {
        [line_number: string]: string[];
    };
    key_clinical_quotes: IClinicalQuote[];
    valid_auth_found: boolean;

    appeal_success_likelihood: AppealSuccessLikelihood;
    likelihood_rationale: string;

    primary_arguments: IAppealArgument[];
    potential_weaknesses: string[];
    payer_specific_considerations: string[];

    proposed_approach?: IProposedApproach | null;

    recommended_justification_template: DenialClass;
    recommended_letter_template: string;
    letter_template_rationale: string;
    selected_letter_template?: string | null;

    next_steps: string;

    iteration: number;
    analyst_feedback_incorporated?: string | null;

    section_labels: {
        [key: string]: string;
    };
    section_display_order: string[];
}

// Message in API/WebSocket format (before conversion to IMessage)
export interface RawMessage {
  id?: string;
  content?: string;
  text?: string;
  role?: 'user' | 'assistant' | 'status' | 'internal';
  sender?: 'me' | 'other';
  created_at?: string;
  files?: any[];
  letter_content?: string;
  agent_name?: string;
  model?: string;
  type?: 'partial' | 'complete' | 'final' | 'stream';
  metadata?: any;
  contextWarning?: boolean;
  // Per-message author (REV-2177), emitted by MessageSerializer for role=user rows.
  author_name?: string;
  author_avatar?: string;
}

export type IMessageMetadata = {
    policies?: IMessageMetadataPolicy[];
    contextWarning?: boolean;
    contentWarning?: boolean; // Backward compatibility for typo
    partial?: boolean; // Indicates if message is partial (chat still in progress)
    abort?: boolean; // Indicates if message is an abort message
    aborted?: boolean; // Assistant frame produced by Stop
    partial_content_length?: number; // Set when that frame includes the streamed text
    ref_id?: string; // Stop user-message id, so a polled row can be placed under that Stop
    user_picture?: string;
    // Document referenced by this message (set on send in ChatStore.askQuestion,
    // echoed back by the API on reload — source of ChatStore.contractPreviewForChat)
    document_id?: string;
    document_title?: string;
    // Set on the first message of a Contract Research / Payer Policy Questions
    // chat (ChatStore.askQuestion) — survives reload, unlike route/channel state,
    // so it's used to gate ChatStore.contractPreviewForChat.
    topic?: string;
    // UI only
    files?: IFileAttachment[]
}

export type IMessageMetadataPolicy = {
    id: number,
    name: string
    source_url: string
    document_id: string
}

export interface AttachedFile {
    name: string;
    content: string;
    uploadResponse?: FileUploadResponse;
    isUploading?: boolean;
    isManuallyAdded?: boolean;
    staticFile?: boolean;
    //
    id?: string;
    state?: 'uploading' | 'uploaded' | 'error'
}

/** Non-production behaviour switches for a chat (REV-2161). Server rejects
 * unknown keys, so this stays a closed set. */
export interface ChatExperiments {
    mock?: boolean;
}

export interface IChannel {
    id?: string | number;
    name: string;
    pinned: boolean;
    label?: 'Policy' | 'Appeal' | 'Claim' | string; // optional user-defined
    description?: string;
    metadata?: Record<string, any>; // Additional metadata for the channel
    tags?: string[]; // Array of channel tags
    agent_name?: string; // Name of the agent that sent the message
    claim_number?: string;
    user_name?: string;
    user_email?: string;
    user_avatar?: string;
    shared?: boolean;
    created_at?: string;
    closed_at?: string | null;
    status?: 'open' | 'closed';
    chat_ref?: string | null; // source chat id when this chat was cloned (REV-1679)
    experiments?: ChatExperiments;
    files_count?: number;
    files?: IFileAttachment[];
    // Last user message saved but never sent to the agent (REV-2553)
    unanswered_message_id?: string | null;
    // UI only
    claim?: ClaimDetailResponse
}

export interface StatusMessageState {
    tool: string;
    action: 'searching' | 'reading' | 'processing' | 'thinking' | 'responding';
    message: string;
    phase: 'start' | 'complete' | 'failed' | 'progress';
    duration: number;
    chat_duration: number;
    timestamp: number;
    files?: IFileAttachment[];
  }

export interface ICategoryCard {
    id: string;
    icon: React.ReactNode | string;
    title: string;
    description: string;
    enabled: boolean;
}

export interface IPolicyPreviewItem {
    document_id: string;
    title: string;
    source_url: string;
    policy_number?: string;
    // A policy document can apply to multiple states at once. The backend has been seen
    // sending a plain string instead of a single-element array for single-state policies --
    // consumers must normalize (see PolicyQuestionsTable.tsx's state column cell).
    state?: string[] | string;
    payer?: string;
    line_of_business?: string;
    // null when the policy has no value for the field -- render the placeholder here, the
    // backend doesn't substitute one. Keep it null on the load-more cursor too:
    // ChatCategoryStore omits start_after_sort_value for null, which the backend reads as
    // "missing" (an "" cursor breaks date sorts and re-matches rows on keyword sorts).
    date_of_business?: string | null;
    expiration_date?: string | null;
    document_type?: string | null;
    plan_name?: string | null;
    plan_policy_id?: string | null;
    hcpcs_codes?: string[];
    icd_10_codes?: string[];
    // Text timestamp ("2026-01-11 01:31:50.877911"), not ISO.
    updated_on?: string | null;
    // Derived server-side from is_active and the effective/expiration dates.
    status?: PolicyStatus;
}

export type PolicyStatus = 'active' | 'expired' | 'upcoming';

export interface IPolicyPreview {
    total: number;
    policies: IPolicyPreviewItem[];
}

/** Column ids and labels for the policy research grid (visibility + pin toggles).
 * The table's original columns show by default; later additions start hidden (visible: false). */
export const POLICY_RESEARCH_TABLE_COLUMN_OPTIONS = [
    { id: 'title', label: 'Policy', required: true, mandatory: true },
    { id: 'status', label: 'Status' },
    { id: 'state', label: 'State' },
    { id: 'payer', label: 'Payer' },
    { id: 'plan_name', label: 'Plan Name', visible: false },
    { id: 'plan_policy_id', label: 'Plan Policy ID', visible: false },
    { id: 'line_of_business', label: 'Line of Business', visible: false },
    { id: 'document_type', label: 'Document Type', visible: false },
    { id: 'date_of_business', label: 'Effective Date' },
    { id: 'expiration_date', label: 'Expiration Date', visible: false },
    { id: 'updated_on', label: 'Updated On', visible: false },
    { id: 'hcpcs_codes', label: 'HCPCS Codes', visible: false },
    { id: 'icd_10_codes', label: 'ICD-10 Codes', visible: false },
] as Array<{
    id: string;
    label: string;
    required?: boolean;
    visible?: boolean;
    mandatory?: boolean;
}>;

/** Column id derived from POLICY_RESEARCH_TABLE_COLUMN_OPTIONS. */
export type PolicyResearchTableColumnId = (typeof POLICY_RESEARCH_TABLE_COLUMN_OPTIONS)[number]['id'];

/** Per-column user preference: visibility and pin (required) state. */
export interface PolicyResearchTableColumnSetting {
    id: PolicyResearchTableColumnId;
    visible: boolean;
    required: boolean;
}

export interface IContractPreviewItem {
    document_id: string;
    title: string;
    state?: string;
    payer?: string;
    line_of_business?: string;
    effective_date?: string;
}

export interface IContractPreview {
    total: number;
    contracts: IContractPreviewItem[];
}

// Status message for tool execution progress
export interface IStatusMessage {
    type: 'status_message';
    chat_id: string;
    details: {
        action: 'searching' | 'reading' | 'processing' | 'thinking' | 'responding';
        tool: string;
        agent_name: string;
        duration: number;      // Tool execution time in seconds
        chat_duration: number; // Time since chat request started
        phase: 'start' | 'complete' | 'failed';
    };
    message: string;  // Human-readable status text
}

export const VALID_ACTIONS: StatusMessageState['action'][] = ['searching', 'reading', 'processing', 'thinking', 'responding'];
export const VALID_PHASES: StatusMessageState['phase'][] = ['start', 'complete', 'failed', 'progress'];